import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { ButtonRow } from '../ButtonRow';
import { ChoiceButton } from './ChoiceButton';
import { ButtonBar } from '../ButtonBar';
import { SvgWrapper } from './SvgWrapper';
import { FullHeightContainer } from '../Containers';
import { TestHeader } from '../TestHeader';

import {
    createEnterExitAnimation,
    createFadeInOutAnimation,
} from '../../transitions';
import { animationDelay } from '../../constants';
import { pushToDataLayer } from '../../utils';

interface IProps {
    className?: string;
    question: string;
    svgComponent?: React.FC;
    stepDisplayNumber: number;
    testDisplayName: string;
    onAnimationExit(): void;
}

const BaseChoiceDisplay: React.FC<IProps> = ({
    className,
    question,
    svgComponent,
    stepDisplayNumber,
    testDisplayName,
    onAnimationExit,
}) => {
    const [componentStage, setComponentStage] = useState('enter');

    useEffect(() => {
        setComponentStage('enter');
    }, [question]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowRight') {
            handleButtonClick(true);
        }

        if (e.key === 'ArrowLeft') {
            handleButtonClick(false);
        }
    };

    const handleButtonClick = (value: boolean) => {
        if (componentStage === 'exit') return;

        pushToDataLayer(
            `${stepDisplayNumber}: ${question}`,
            value ? 'Ja' : 'Nee'
        );
        setComponentStage('exit');
    };

    const handleAnimationExit = () => {
        if (componentStage === 'exit') {
            onAnimationExit();
        }
    };

    const firstAnimation = createEnterExitAnimation({ stage: componentStage });
    const secondAnimation = createEnterExitAnimation({
        stage: componentStage,
        delay: animationDelay.second,
    });
    const thirdAnimation = createFadeInOutAnimation({
        stage: componentStage,
        delay: animationDelay.third,
    });
    const fourthAnimation = createFadeInOutAnimation({
        stage: componentStage,
        delay: animationDelay.fourth,
        onRest: handleAnimationExit,
    });

    const SvgComponent = svgComponent;

    return (
        <>
            <FullHeightContainer className={className}>
                <TestHeader style={firstAnimation}>{question}</TestHeader>
                {SvgComponent && (
                    <SvgWrapper style={secondAnimation}>
                        <SvgComponent />
                    </SvgWrapper>
                )}
            </FullHeightContainer>
            <ButtonBar>
                <ButtonRow>
                    <ChoiceButton
                        style={thirdAnimation}
                        onClick={() => handleButtonClick(false)}
                    >
                        nee
                    </ChoiceButton>
                    <ChoiceButton
                        style={fourthAnimation}
                        onClick={() => handleButtonClick(true)}
                    >
                        ja
                    </ChoiceButton>
                </ButtonRow>
            </ButtonBar>
        </>
    );
};

export const ChoiceDisplay = styled(BaseChoiceDisplay)`
    padding-top: 80px;

    ${SvgWrapper} {
        flex: 1;
    }
`;
